/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox の白板チーム定義と、戦闘力サマリー API の memberTeamMap から
 *  各チームの所属メンバー行を組み立てるロジック。チームキー/ラベルは ProjectsView の
 *  TEAM_OPTIONS・候補案件プールと揃える（PDG / ACE airCloset / ACE Platform / ACE Mall）。
 *  組み立て時に EXCLUDED_NICKNAMES（PJ 計画対象外のニックネーム）を除外するため、
 *  除外セットに載るメンバーはどの白板にも現れない。
 * @graph-connects none
 */
import {
  ACE_AIRCLOSET_TEAM,
  ACE_MALL_TEAM,
  ACE_PLATFORM_TEAM,
  PDG_TEAMS,
} from '@cortex/domain-product-innovation-lab-combat-power-scope';
import type { PjSandboxBoardKey } from '@cortex/domain-product-innovation-lab-types';
import type { MemberTeamMapping } from '../../../../types/combat-power.js';
import type { SandboxMember, SandboxTeam } from './types.js';

/** 白板 1 枚（チーム）の定義。key/label は既存の候補案件プールと揃える。 */
interface TeamDef {
  /**
   * 一意な key（タブ切替・候補案件の teamKey と一致）。共有保存の Firestore
   * ドキュメント ID とサーバー側の許可値も兼ねるため、型は共有 SSOT
   * (`PJ_SANDBOX_BOARD_KEYS`) の `PjSandboxBoardKey` に固定する。ここに新しい白板を
   * 足すときは SSOT 側の配列も更新しないとコンパイルが通らない。
   */
  key: PjSandboxBoardKey;
  /** 表示ラベル。 */
  label: string;
  /** memberTeamMap の team_name がこのチームに属するか判定する。 */
  match: (teamName: string) => boolean;
}

/**
 * 白板の対象外にするニックネーム（小文字）。BSE 等の PJ 計画対象外メンバーを除外する。
 * @graph-connects none
 */
const EXCLUDED_NICKNAMES: ReadonlySet<string> = new Set([
  'miguel',
  'hiro',
  'punna',
  'yukito',
  'kai',
]);

/** 白板チーム定義（順序 = タブの並び）。 @graph-connects none */
export const TEAM_DEFS: readonly TeamDef[] = [
  // PDG = pi_pdg グループ（pi-tet / pi_sret）。SSOT の PDG_TEAMS で判定する。
  { key: 'pdg', label: 'PDG', match: (team) => PDG_TEAMS.has(team) },
  // ACE サブチームの team_name も SSOT (member-scope) のリテラルを参照する（重複宣言禁止）。
  { key: 'ace-ac', label: 'ACE airCloset', match: (team) => team === ACE_AIRCLOSET_TEAM },
  { key: 'ace-platform', label: 'ACE Platform', match: (team) => team === ACE_PLATFORM_TEAM },
  { key: 'ace-mall', label: 'ACE Mall', match: (team) => team === ACE_MALL_TEAM },
];

/**
 * 戦闘力サマリー API の memberTeamMap から白板チーム（メンバー行）を組み立てる。
 * 各メンバーの表示名・ニックネームには memberTeamMap.name（小文字ニックネーム）を使う
 * （ガイドライン「ユーザー名はニックネーム優先」）。ニックネーム昇順に並べる。
 * ニックネームが EXCLUDED_NICKNAMES に含まれるメンバー（PJ 計画対象外の BSE 等）は
 * どの白板からも除外する。
 * @graph-connects none
 */
export function buildTeamsFromMemberMap(memberTeamMap: MemberTeamMapping[]): SandboxTeam[] {
  return TEAM_DEFS.map((def) => {
    const members: SandboxMember[] = memberTeamMap
      .filter((m) => def.match(m.team))
      .map((m) => m.name.trim())
      .filter((name) => name.length > 0 && !EXCLUDED_NICKNAMES.has(name.toLowerCase()))
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: `${def.key}-${name}`, name, nickName: name }));
    return { key: def.key, label: def.label, members };
  });
}
